import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk, type User, type Settings } from '@mosaferati/api-client';
import { api } from '@/lib/api.ts';
import { q } from '@/lib/queries.ts';
import { authEvents } from '@/lib/auth-events.ts';
import { queryClient } from '@/app/query-client.ts';

export type Me = { user: User; settings: Settings } | null;

/** Current user (null when logged out). Cached; refetched on focus. */
export function useMe() {
  const qc = useQueryClient();
  const query = useQuery(q.me());
  useEffect(() => {
    const off = authEvents.on('unauthenticated', () => qc.setQueryData(qk.me(), null));
    return () => {
      off();
    };
  }, [qc]);
  return { me: (query.data ?? null) as Me, isLoading: query.isLoading, refetch: query.refetch };
}

/** Resolve the current user once (route guards). */
export async function ensureMe(): Promise<Me> {
  try {
    return ((await queryClient.ensureQueryData(q.me())) ?? null) as Me;
  } catch {
    return null;
  }
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (all: boolean) => {
      await api.POST('/auth/logout', { body: { all_devices: all } });
    },
    onSettled: () => {
      qc.clear(); // drops the persisted per-user cache too
      qc.setQueryData(qk.me(), null);
    },
  });
}
